.main {
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  padding-bottom: 4rem;
}

.bgGradient {
  position: fixed;
  inset: 0;
  background:
    radial-gradient(ellipse 80% 50% at 50% -20%, rgba(34, 211, 238, 0.08), transparent),
    radial-gradient(ellipse 60% 40% at 100% 100%, rgba(251, 146, 60, 0.05), transparent),
    radial-gradient(ellipse 40% 30% at 0% 80%, rgba(34, 211, 238, 0.05), transparent);
  pointer-events: none;
  z-index: 0;
}

.bgGradient::after {
  content: "";
  position: fixed;
  inset: 0;
  pointer-events: none;
  background:
    repeating-linear-gradient(
      0deg, transparent, transparent 49px,
      rgba(148, 163, 184, 0.02) 49px, rgba(148, 163, 184, 0.02) 50px
    ),
    repeating-linear-gradient(
      90deg, transparent, transparent 49px,
      rgba(148, 163, 184, 0.02) 49px, rgba(148, 163, 184, 0.02) 50px
    );
}

.header {
  position: relative;
  z-index: 1;
  max-width: 720px;
  margin: 0 auto;
  padding: 1.5rem 2rem;
}

.backLink {
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.2s;
}

.backLink:hover {
  color: var(--text-primary);
}

/* Blog index page */
.hero {
  position: relative;
  z-index: 1;
  text-align: center;
  padding: 3rem 2rem 2rem;
}

.title {
  font-family: var(--font-heading);
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 700;
  margin: 0 0 0.75rem;
  color: var(--text-accent);
}

.subtitle {
  color: var(--text-secondary);
  font-size: 1.1rem;
  margin: 0;
}

.grid {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  max-width: 720px;
  margin: 2.5rem auto 0;
  padding: 0 2rem;
}

.card {
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 16px;
  padding: 2rem 2.25rem;
  backdrop-filter: blur(12px);
  box-shadow: 0 2px 12px var(--shadow-color);
  text-decoration: none;
  display: block;
  transition: border-color 0.25s, box-shadow 0.25s, transform 0.25s;
}

.card:hover {
  border-color: rgba(34, 211, 238, 0.3);
  box-shadow: var(--card-shadow-hover);
  transform: translateY(-2px);
}

.cardTitle {
  font-family: var(--font-heading);
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--accent-warm);
  margin: 0 0 0.5rem;
  line-height: 1.3;
}

.cardMeta {
  display: flex;
  gap: 1rem;
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin-bottom: 0.75rem;
}

.cardDescription {
  color: var(--text-secondary);
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
}

/* Blog post page */
.article {
  position: relative;
  z-index: 1;
  max-width: 720px;
  margin: 0 auto;
  padding: 2rem 2rem 4rem;
}

.articleTitle {
  font-family: var(--font-heading);
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: 700;
  color: var(--text-accent);
  margin: 0 0 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid var(--text-accent);
  line-height: 1.2;
}

.articleMeta {
  display: flex;
  gap: 1rem;
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin-bottom: 2.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--card-border);
}

/* Markdown content styles */
.content h2 {
  font-family: var(--font-heading);
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--accent-warm);
  margin: 2.5rem 0 1rem;
  line-height: 1.3;
}

.content h3 {
  font-family: var(--font-heading);
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-secondary);
  margin: 2rem 0 0.75rem;
}

.content p {
  color: var(--text-secondary);
  font-size: 1rem;
  line-height: 1.75;
  margin: 0 0 1.25rem;
}

.content a {
  color: var(--text-accent);
  text-decoration: none;
  transition: opacity 0.2s;
}

.content a:hover {
  opacity: 0.8;
  text-decoration: underline;
}

.content ul,
.content ol {
  color: var(--text-secondary);
  font-size: 1rem;
  line-height: 1.75;
  margin: 0 0 1.25rem;
  padding-left: 1.5rem;
}

.content li {
  margin-bottom: 0.4rem;
}

.content strong {
  color: var(--text-primary);
  font-weight: 600;
}

.content em {
  font-style: italic;
}

.content blockquote {
  border-left: 3px solid var(--text-accent);
  margin: 1.5rem 0;
  padding: 0.75rem 1.25rem;
  background: rgba(34, 211, 238, 0.05);
  border-radius: 0 8px 8px 0;
}

.content blockquote p {
  margin: 0;
}

.content code {
  background: rgba(148, 163, 184, 0.1);
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  font-size: 0.9em;
  color: var(--text-accent);
}

.content pre {
  background: var(--bg-secondary);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 1rem 1.25rem;
  overflow-x: auto;
  margin: 0 0 1.25rem;
}

.content pre code {
  background: none;
  padding: 0;
  border-radius: 0;
  font-size: 0.875rem;
  color: var(--text-primary);
}

.content table {
  width: 100%;
  border-collapse: collapse;
  margin: 1.5rem 0;
  font-size: 0.9rem;
}

.content th {
  text-align: left;
  padding: 0.75rem;
  border-bottom: 2px solid var(--card-border);
  color: var(--text-primary);
  font-weight: 600;
}

.content td {
  padding: 0.75rem;
  border-bottom: 1px solid var(--card-border);
  color: var(--text-secondary);
}

.content hr {
  border: none;
  border-top: 1px solid var(--card-border);
  margin: 2.5rem 0;
}

/* CTA section at bottom of posts */
.cta {
  position: relative;
  z-index: 1;
  max-width: 720px;
  margin: 0 auto;
  padding: 0 2rem 2rem;
}

.ctaCard {
  background: var(--card-bg);
  border: 1px solid rgba(34, 211, 238, 0.2);
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
}

.ctaTitle {
  font-family: var(--font-heading);
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.5rem;
}

.ctaText {
  color: var(--text-secondary);
  font-size: 0.95rem;
  margin: 0 0 1.25rem;
}

.ctaButtons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.ctaButton {
  display: inline-block;
  padding: 0.65rem 1.5rem;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s;
  background: var(--text-accent);
  color: #0b1120;
}

.ctaButton:hover {
  filter: brightness(1.1);
  transform: translateY(-1px);
}

.ctaButtonSecondary {
  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-primary);
}

.ctaButtonSecondary:hover {
  border-color: var(--text-accent);
  color: var(--text-accent);
}

/* Footer */
.footer {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: center;
  gap: 2rem;
  padding: 3rem 2rem 1rem;
  font-size: 0.85rem;
}

.footer a {
  color: var(--text-secondary);
  text-decoration: none;
  transition: color 0.2s;
}

.footer a:hover {
  color: var(--text-accent);
}

/* Mobile */
@media (max-width: 600px) {
  .hero {
    padding: 2rem 1rem 1.5rem;
  }

  .grid {
    padding: 0 1rem;
  }

  .card {
    padding: 1.5rem;
  }

  .article {
    padding: 1.5rem 1rem 3rem;
  }

  .header {
    padding: 1rem;
  }

  .footer {
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    padding: 2rem 1rem 1rem;
  }

  .cta {
    padding: 0 1rem 2rem;
  }

  .ctaButtons {
    flex-direction: column;
    align-items: stretch;
  }

  .ctaButton {
    min-height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .content table {
    font-size: 0.8rem;
  }

  .content th,
  .content td {
    padding: 0.5rem;
  }
}
